/**
 * Merge, revision parts and restore as fixture data (W9 §C.5).
 *
 * One clean document (`doc-2`, one author, never diverged), one diverged document (`doc-1`,
 * three distinct revision authors), and a 4-part preview with one `conflict:true` row. The
 * gates mirror the server's own refusals so a permission or a sequencing bug fails offline:
 * `open` throws `ForbiddenError` unless the viewer may update the document; `commit` throws
 * `MergeUnresolvedError(n)` while any part that actually needs a decision (`conflict` or any
 * side `changed`) has none recorded — the same rule the real 409 enforces.
 */
import { ApiError, ForbiddenError, MergeUnresolvedError, NotFoundError } from "~/api/errors";
import type {
  Divergence,
  MergeAudit,
  MergeCommitResult,
  MergeDecision,
  MergePartDiff,
  MergeSession,
  Revision,
  RevisionPart,
} from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const daysAgo = (d: number) => new Date(BASE_TIME - d * 86_400_000).toISOString();
const hoursAgo = (h: number) => new Date(BASE_TIME - h * 3600_000).toISOString();

/** The one document with three distinct revision authors — the divergence-badge fixture. */
export const FAKE_DIVERGED_DOCUMENT_ID = "doc-1";
/** A document with a single author — never diverged, proves the badge stays hidden. */
export const FAKE_CLEAN_DOCUMENT_ID = "doc-2";

interface FixtureRevision {
  id: string;
  documentId: string;
  title: string;
  summary: string;
  createdAt: string;
  createdBy: { id: string; name: string; avatarUrl: string | null };
}

const REVISIONS: FixtureRevision[] = [
  {
    id: "rev-div-1",
    documentId: FAKE_DIVERGED_DOCUMENT_ID,
    title: "Access review runbook",
    summary: "Initial draft",
    createdAt: daysAgo(5),
    createdBy: { id: "user-1", name: "Ada Lovelace", avatarUrl: null },
  },
  {
    id: "rev-div-2",
    documentId: FAKE_DIVERGED_DOCUMENT_ID,
    title: "Access review runbook",
    summary: "Added the escalation section",
    createdAt: daysAgo(3),
    createdBy: { id: "user-2", name: "Grace Hopper", avatarUrl: null },
  },
  {
    id: "rev-div-3",
    documentId: FAKE_DIVERGED_DOCUMENT_ID,
    title: "Access review runbook",
    summary: "Rewrote the rollback steps",
    createdAt: hoursAgo(6),
    createdBy: { id: "user-3", name: "Alan Turing", avatarUrl: null },
  },
  {
    id: "rev-clean-1",
    documentId: FAKE_CLEAN_DOCUMENT_ID,
    title: "Onboarding checklist",
    summary: "First pass",
    createdAt: daysAgo(10),
    createdBy: { id: "user-1", name: "Ada Lovelace", avatarUrl: null },
  },
  {
    id: "rev-clean-2",
    documentId: FAKE_CLEAN_DOCUMENT_ID,
    title: "Onboarding checklist",
    summary: "Added the laptop-setup step",
    createdAt: daysAgo(2),
    createdBy: { id: "user-1", name: "Ada Lovelace", avatarUrl: null },
  },
];

/** partId `part-3` is the one `conflict:true` row the fixture guarantees. */
const PART_IDS = ["part-1", "part-2", "part-3", "part-4"] as const;

interface SessionRow {
  id: string;
  documentId: string;
  baseRevisionId: string;
  contributingRevisionIds: string[];
  status: "open" | "committed";
  openedById: string;
  openedAt: string;
  resultRevisionId: string | null;
  resolutions: Map<string, { decision: MergeDecision; chosenFrom?: string }>;
}

export interface FakeMergeDeps {
  requireSession(): unknown;
  delay<T>(value: T): Promise<T>;
  viewerId(): string;
  /** Mirrors the real `IAccessResolver` check `MergeController.open` makes. */
  canUpdateDocument(documentId: string): boolean;
  documentExists(documentId: string): boolean;
}

export function createFakeMerge(deps: FakeMergeDeps) {
  let sessionSeq = 0;
  const sessions = new Map<string, SessionRow>();
  const divergedResolved = new Set<string>();

  const revisionsFor = (documentId: string): FixtureRevision[] =>
    REVISIONS.filter((revision) => revision.documentId === documentId);

  const findSession = (sessionId: string): SessionRow => {
    const found = sessions.get(sessionId);
    if (!found) {
      throw new NotFoundError("Merge session not found");
    }
    return found;
  };

  /** One row per fixed partId; `sides` has one entry per contributing revision id. */
  const previewFor = (session: SessionRow): MergePartDiff[] =>
    PART_IDS.map((partId, index) => {
      const conflict = partId === "part-3";
      return {
        partId,
        partType: "paragraph",
        baseContent: null,
        conflict,
        sides: session.contributingRevisionIds.map((revisionId, sideIndex) => {
          const revision = REVISIONS.find((r) => r.id === revisionId);
          // Every part changes on at least one side except part-4, which is untouched by
          // either contributor — proving a part that needs NO decision is not counted against
          // `commit`'s unresolved gate.
          const changed =
            partId !== "part-4" &&
            (conflict || sideIndex === index % session.contributingRevisionIds.length);
          return {
            revisionId,
            changed,
            createdAt: revision?.createdAt ?? new Date(BASE_TIME).toISOString(),
            content: changed
              ? {
                  type: "paragraph",
                  content: [{ type: "text", text: `${partId} from ${revisionId}` }],
                }
              : null,
            author: {
              kind: "human" as const,
              userId: revision?.createdBy.id ?? null,
              aiModel: null,
            },
          };
        }),
      };
    });

  return {
    divergence: async (documentId: string): Promise<Divergence> => {
      deps.requireSession();
      if (!deps.documentExists(documentId)) {
        throw new NotFoundError("Document not found");
      }
      if (documentId === FAKE_DIVERGED_DOCUMENT_ID && !divergedResolved.has(documentId)) {
        return deps.delay({
          documentId,
          divergent: true,
          distinctAuthors: 3,
          sinceRevisionId: "rev-div-1",
        });
      }
      return deps.delay({
        documentId,
        divergent: false,
        distinctAuthors: 1,
        sinceRevisionId: null,
      });
    },

    open: async (input: {
      documentId: string;
      contributingRevisionIds: string[];
    }): Promise<MergeSession> => {
      deps.requireSession();
      if (!deps.documentExists(input.documentId)) {
        throw new NotFoundError("Document not found");
      }
      if (!deps.canUpdateDocument(input.documentId)) {
        throw new ForbiddenError("You cannot merge this document");
      }
      if (input.contributingRevisionIds.length < 2) {
        throw new ApiError("At least two contributing revisions are required.", 400);
      }
      sessionSeq += 1;
      const id = `merge-session-${sessionSeq}`;
      const docRevisions = revisionsFor(input.documentId);
      const base = docRevisions[0];
      const [firstContributing] = input.contributingRevisionIds;
      if (!firstContributing) throw new Error("A merge needs at least one contributing revision.");
      const row: SessionRow = {
        id,
        documentId: input.documentId,
        baseRevisionId: base?.id ?? firstContributing,
        contributingRevisionIds: input.contributingRevisionIds,
        status: "open",
        openedById: deps.viewerId(),
        openedAt: new Date(BASE_TIME).toISOString(),
        resultRevisionId: null,
        resolutions: new Map(),
      };
      sessions.set(id, row);
      return deps.delay({
        id: row.id,
        documentId: row.documentId,
        baseRevisionId: row.baseRevisionId,
        contributingRevisionIds: row.contributingRevisionIds,
        status: row.status,
        openedById: row.openedById,
        openedAt: row.openedAt,
        resultRevisionId: row.resultRevisionId,
      });
    },

    preview: async (sessionId: string): Promise<MergePartDiff[]> => {
      deps.requireSession();
      const session = findSession(sessionId);
      return deps.delay(previewFor(session));
    },

    resolvePart: async (input: {
      sessionId: string;
      partId: string;
      decision: MergeDecision;
      chosenFrom?: string;
      combinedContent?: string;
    }): Promise<void> => {
      deps.requireSession();
      const session = findSession(input.sessionId);
      if (session.status !== "open") {
        throw new ApiError("Merge session is not open.", 409);
      }
      if (!["accept", "reject", "combine"].includes(input.decision)) {
        throw new ApiError("decision must be accept | reject | combine.", 400);
      }
      if (input.decision === "accept" && !input.chosenFrom) {
        throw new ApiError("chosenFrom is required when decision is accept.", 400);
      }
      if (input.decision === "combine" && !input.combinedContent) {
        throw new ApiError("combinedContent is required when decision is combine.", 400);
      }
      session.resolutions.set(input.partId, {
        decision: input.decision,
        ...(input.chosenFrom ? { chosenFrom: input.chosenFrom } : {}),
      });
      return deps.delay(undefined);
    },

    commit: async (sessionId: string): Promise<MergeCommitResult> => {
      deps.requireSession();
      const session = findSession(sessionId);
      const preview = previewFor(session);
      const needsDecision = preview.filter(
        (part) => part.conflict || part.sides.some((side) => side.changed),
      );
      const unresolved = needsDecision.filter((part) => !session.resolutions.has(part.partId));
      if (unresolved.length > 0) {
        throw new MergeUnresolvedError(unresolved.length);
      }
      let accepted = 0;
      let rejected = 0;
      let combined = 0;
      for (const resolution of session.resolutions.values()) {
        if (resolution.decision === "accept") accepted += 1;
        else if (resolution.decision === "reject") rejected += 1;
        else combined += 1;
      }
      session.status = "committed";
      sessionSeq += 1;
      session.resultRevisionId = `rev-merged-${sessionSeq}`;
      divergedResolved.add(session.documentId);
      return deps.delay({
        resultRevisionId: session.resultRevisionId,
        accepted,
        rejected,
        combined,
        unresolved: 0,
      });
    },

    audit: async (sessionId: string): Promise<MergeAudit> => {
      deps.requireSession();
      const session = findSession(sessionId);
      const resolutions = [...session.resolutions.entries()].map(([partId, resolution]) => ({
        partId,
        decision: resolution.decision,
        chosenFromRevisionId: resolution.chosenFrom ?? null,
        resolvedById: session.openedById,
        resolvedAt: new Date(BASE_TIME).toISOString(),
        contributors: [{ kind: "human" as const, userId: session.openedById, aiModel: null }],
      }));
      return deps.delay({ resolutions, unattributed: 0 });
    },

    revisionParts: async (revisionId: string): Promise<RevisionPart[]> => {
      deps.requireSession();
      // M11: skipped for the synthetic "latest" id.
      if (revisionId === "latest") {
        return deps.delay([]);
      }
      const revision = REVISIONS.find((r) => r.id === revisionId);
      if (!revision) {
        throw new NotFoundError("Revision not found");
      }
      return deps.delay(
        PART_IDS.map((partId, index) => ({
          id: `${revisionId}-${partId}`,
          revisionId,
          partId,
          partType: "paragraph",
          sequence: index,
          contentHash: `hash-${revisionId}-${partId}`,
          data: {
            type: "paragraph",
            content: [{ type: "text", text: `${partId} of ${revision.title}` }],
          },
          introducedInRevisionId: revision.id,
          author: {
            kind: "human" as const,
            userId: revision.createdBy.id,
            aiModel: null,
            triggeredByUserId: null,
          },
        })),
      );
    },

    restoreRevision: async (input: {
      documentId: string;
      revisionId: string;
      partId?: string;
    }): Promise<{ restoredRevisionId: string }> => {
      deps.requireSession();
      if (!deps.documentExists(input.documentId)) {
        throw new NotFoundError("Document not found");
      }
      if (!deps.canUpdateDocument(input.documentId)) {
        throw new ForbiddenError("You cannot restore this document");
      }
      // A deterministic trigger for the 502 "the live session refused it" outcome (§B.2), so a
      // test can reach it without depending on real collaboration-server behaviour.
      if (input.partId === "part-collab-fail") {
        throw new ApiError("Restore could not be applied to the live session.", 502);
      }
      sessionSeq += 1;
      return deps.delay({ restoredRevisionId: `rev-restored-${sessionSeq}` });
    },

    revisionsDiff: async (input: {
      id: string;
      compareToId?: string;
      partId?: string;
    }): Promise<unknown> => {
      deps.requireSession();
      return deps.delay({
        id: input.id,
        compareToId: input.compareToId ?? null,
        partId: input.partId ?? null,
        summary: "Fixture diff — one paragraph changed.",
      });
    },

    revisionInfo: async (revisionId: string): Promise<Revision> => {
      deps.requireSession();
      const revision = REVISIONS.find((r) => r.id === revisionId);
      if (!revision) {
        throw new NotFoundError("Revision not found");
      }
      return deps.delay({ ...revision });
    },
  };
}
